import { zodResolver } from "@hookform/resolvers/zod";
import type {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues,
  UseFormHandleSubmit,
  UseFormProps,
  UseFormReturn,
} from "react-hook-form";
import { useController, useForm } from "react-hook-form";
import type { StrictOmit } from "ts-essentials";
import type * as z from "zod";

type NullableFields<TFieldValues extends FieldValues> = {
  [Key in keyof TFieldValues]: TFieldValues[Key] extends ReadonlyArray<any>
    ? TFieldValues[Key]
    : TFieldValues[Key] | null;
};

export type StudioFormValues<TFieldValues extends FieldValues> =
  NullableFields<TFieldValues>;

export interface UseStudioFormProps<
  TSchema extends z.ZodType<FieldValues, z.ZodTypeDef, unknown>,
> extends StrictOmit<
    UseFormProps<NullableFields<z.infer<TSchema>>, void>,
    "resolver" | "defaultValues"
  > {
  schema: TSchema;
  defaultValues?: NullableFields<z.infer<TSchema>>;
  onSubmit: Parameters<
    UseFormHandleSubmit<NullableFields<z.infer<TSchema>>, z.infer<TSchema>>
  >[0];
}

export function useField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  shouldUnregister,
}: {
  control: Control<TFieldValues>;
  name: TName;
  shouldUnregister?: boolean;
}): {
  value: FieldPathValue<TFieldValues, TName>;
  onChange: (value: unknown) => void;
  errorMessage: string | undefined;
} {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ control, name, shouldUnregister });

  return { value, onChange, errorMessage: error?.message };
}

export interface UseStudioFormReturn<TSchemaOutput extends FieldValues>
  extends StrictOmit<
    UseFormReturn<NullableFields<TSchemaOutput>, void, TSchemaOutput>,
    "handleSubmit"
  > {
  handleSubmit: ReturnType<
    UseFormHandleSubmit<NullableFields<TSchemaOutput>, TSchemaOutput>
  >;
}

export function useStudioForm<
  TSchema extends z.ZodType<FieldValues, z.ZodTypeDef, unknown>,
>({
  schema,
  onSubmit,
  ...props
}: UseStudioFormProps<TSchema>): UseStudioFormReturn<z.infer<TSchema>> {
  const { handleSubmit, ...form } = useForm<
    NullableFields<z.infer<TSchema>>,
    void,
    z.infer<TSchema>
  >({
    ...(props as UseFormProps<NullableFields<z.infer<TSchema>>>),
    // TODO: Defaulting to sync parsing may improve validation time
    //       for large forms (assuming there are no async validations)
    resolver: zodResolver(schema),
  });

  return {
    ...form,
    handleSubmit: handleSubmit(onSubmit),
  };
}
